import React, {useEffect, useState} from "react";
import {Button, Modal} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faExclamationCircle, faInfoCircle} from "@fortawesome/free-solid-svg-icons";
import ModalControls from "../../common/ModalControls";
import {useLocation, useNavigate} from "react-router-dom";
import {defaultFormState} from "../../../../defaults/Forms";
import {attractionDefault} from "../../../../defaults/Items";
import {
  compareData,
  onCloseDetails,
  onItemsListDeleteButtonClick,
  onSaveAndClose,
  restoreFormData
} from "../../../../helpers/ComponentHelper";
import AppComponentCardHeader from "../../common/AppComponentCardHeader";
import LoadingDataDiv from "../../common/LoadingDataDiv";
import DeleteWarningModal from "../../common/DeleteWarningModal";
import TextInput from "../../../elements/TextInput";
import TextArea from "../../../elements/TextArea";
import useCrud from "../../../../hooks/useCrud";
import {Table} from "../../../table/Table";
import "../../../../css/DictionaryForms.css";
import "../../../../css/ResourceForms.css";
import "../../../../css/ResourceTables.css";

const Attraction = () => {
  const dataUrl = "/attractions";
  const defaultItem = attractionDefault;
  const itemName = "atrakcja";
  const navigate = useNavigate();
  const location = useLocation();
  const {createItem, updateItem, deleteItem, getItems} = useCrud(dataUrl);

  const [loading, setLoading] = useState(true);
  const [allowDelete, setAllowDelete] = useState(null);
  const [currentFormState, setCurrentFormState] = useState(defaultFormState);
  const [itemsList, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(defaultItem);
  const [backupItem, setBackupItem] = useState(defaultItem);
  const [itemChanged, setItemChanged] = useState(false);
  const [updateConflict, setUpdateConflict] = useState(false);

  const columns = [
    {label: "Id", accessor: "id", sortable: true, searchable: false, visible: false},
    {label: "Nazwa", accessor: "name", sortable: true, searchable: true},
    {label: "Skrócona nazwa", accessor: "shortName", sortable: true, searchable: true},
    {label: "Opis", accessor: "description", sortable: true, searchable: true},
    {label: "Edycja", accessor: "editBtn", sortable: false, searchable: false},
    {label: "Usuń", accessor: "deleteBtn", sortable: false, searchable: false}
  ];

  const state = {
    itemsList,
    setItems,
    currentItem,
    setCurrentItem,
    currentFormState,
    setCurrentFormState,
    defaultItem,
    backupItem,
    setBackupItem,
    itemChanged,
    setItemChanged,
    setAllowDelete,
    setRelatedItems: null
  };

  const onDelete = async () => {
    const response = await deleteItem(`${dataUrl}/${currentItem.id}`, currentItem.id, state);
    if (response === 401 || response === 403) {
      navigate("/login", {state: {from: location}, replace: true});
    }
  };

  const onSaveItemClick = async event => {
    event.preventDefault();
    if (!currentItem.name || !currentItem.shortName) {
      if (!currentItem.name) {
        const input = document.getElementById("name");
        input?.classList.add("form-input-invalid");
        if (input) input.placeholder = "ten element jest wymagany";
      }
      if (!currentItem.shortName) {
        const input = document.getElementById("shortName");
        input?.classList.add("form-input-invalid");
        if (input) input.placeholder = "ten element jest wymagany";
      }
      return;
    }

    const item = {
      id: currentItem.id,
      shortName: currentItem.shortName,
      name: currentItem.name,
      description: currentItem.description
    };

    const response = currentFormState.formAddingDataMode
      ? await createItem(dataUrl, item, state)
      : await updateItem(`${dataUrl}/${item.id}`, item, state);

    if (response.status === 401 || response.status === 403) {
      navigate("/login", {state: {from: location}, replace: true});
    } else if (response.status === 409) {
      setUpdateConflict(true);
    } else if (response) {
      onSaveAndClose({state});
    }
  };

  const onClose = () => {
    setUpdateConflict(false);
    onCloseDetails({state});
  };

  useEffect(() => {
    if (updateConflict) setUpdateConflict(false);
    compareData(currentFormState, setCurrentFormState, currentItem, backupItem);
  }, [itemChanged]);

  useEffect(() => {
    if (allowDelete !== null) {
      onItemsListDeleteButtonClick(currentFormState, setCurrentFormState, itemName, allowDelete, currentItem.name);
    }
  }, [allowDelete]);

  useEffect(() => {
    const getData = async () => {
      const response = await getItems(dataUrl);
      if (response.status === 200) {
        setItems(response.data);
        setLoading(false);
      } else if (response.status === 401 || response.status === 403) {
        navigate("/login", {state: {from: location}, replace: true});
      } else {
        alert("Nie udało się pobrać danych.");
      }
    };
    getData();
  }, []);

  const getCompleteItem = async id => {
    const response = await getItems(`${dataUrl}/${id}`);
    if (response.status === 200) return response.data;
    if (response.status === 401 || response.status === 403) {
      navigate("/login", {state: {from: location}, replace: true});
    } else {
      alert("Nie udało się pobrać danych.");
    }
  };

  const addDataButtonProps = {
    setCurrentItem,
    setBackupItem,
    defaultItem,
    currentFormState,
    setCurrentFormState,
    formDescription: "",
    formHeader: "Nowa atrakcja",
    buttonTitle: "Dodaj atrakcję"
  };

  let dataSectionContent;
  if (loading) {
    dataSectionContent = <LoadingDataDiv/>;
  } else if (itemsList.length > 0) {
    dataSectionContent = (
      <Table
        rows={itemsList}
        columns={columns}
        state={state}
        tableClassName="resource-object-table attraction-table"
        tableContainerClassName="resource-object-table-scroll"
        checkRelatedItems={() => setAllowDelete(true)}
        formHeader={`Edycja ${itemName}`}
        relatedItemsUrl={null}
        getCompleteItem={getCompleteItem}
        exportFileName="atrakcje.xlsx"
      />
    );
  } else {
    dataSectionContent = (
      <div className="resource-empty-state">
        <FontAwesomeIcon icon={faExclamationCircle}/>
        <span>Nie znaleziono żadnych atrakcji. Dodaj pierwszy wpis.</span>
      </div>
    );
  }

  const modalTitle = currentFormState.formAddingDataMode
    ? "Nowa atrakcja"
    : `Edycja atrakcji: ${currentItem.name || "bez nazwy"}`;

  return (
    <div id="layoutSidenav_content" className="dictionary-page resource-page">
      <AppComponentCardHeader title="Atrakcje" buttonProps={addDataButtonProps}/>

      <div className="container-fluid px-4 py-3">
        <div className="card shadow-sm mb-5 resource-list-card resource-object-list-card attraction-list-card">
          {dataSectionContent}
        </div>
      </div>

      <Modal
        show={currentFormState.showForm}
        onHide={onClose}
        backdrop="static"
        keyboard={false}
        className="dictionary-form-modal"
        dialogClassName="dictionary-form-modal-dialog resource-form-modal-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>{modalTitle}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="dictionary-form-intro">
            <div>
              <strong>{currentFormState.formAddingDataMode ? "Dodajesz nową atrakcję" : "Edytujesz atrakcję"}</strong>
              <span>Uzupełnij podstawowe informacje. Pola oznaczone gwiazdką są wymagane.</span>
            </div>
            {!currentFormState.formAddingDataMode && (
              <Button size="sm" variant="outline-secondary" onClick={() => {
                setUpdateConflict(false);
                restoreFormData({state});
              }}>
                Przywróć wartości
              </Button>
            )}
          </div>

          <form id="add-attraction-form" name="add-attraction-form" className="dictionary-form-grid">
            <div className="dictionary-form-field">
              <label htmlFor="name"><span className="required">*</span> Nazwa</label>
              <TextInput propertyName="name" required={true} state={state}/>
              <small>Pełna nazwa atrakcji.</small>
            </div>
            <div className="dictionary-form-field dictionary-form-field-short">
              <label htmlFor="shortName"><span className="required">*</span> Skrót</label>
              <TextInput propertyName="shortName" maxlen="10" required={true} state={state}/>
              <small>Maksymalnie 10 znaków.</small>
            </div>
            <div className="dictionary-form-field dictionary-form-field-full">
              <label htmlFor="description">Opis</label>
              <TextArea propertyName="description" required={false} rows="4" state={state}/>
            </div>
          </form>

          {updateConflict && (
            <div className="dictionary-form-error">
              <FontAwesomeIcon icon={faInfoCircle}/>
              <span>Nie można zapisać atrakcji, ponieważ istnieje już wpis o tej nazwie lub skrócie.</span>
            </div>
          )}
        </Modal.Body>
        <ModalControls onDelete={onDelete} onCloseDetails={onClose} onSubmit={onSaveItemClick} state={state}/>
      </Modal>

      <DeleteWarningModal state={state} onDelete={onDelete} deleteItemName={itemName}/>
    </div>
  );
};

export default Attraction;
